// src/components/profile/ChangePasswordForm.js
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

const ChangePasswordForm = ({ onSave, onCancel, loading }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [errors, setErrors] = useState({});

  const passwordRequirements = {
    minLength: formData.newPassword.length >= 8,
    hasUpperCase: /[A-Z]/.test(formData.newPassword),
    hasLowerCase: /[a-z]/.test(formData.newPassword),
    hasNumber: /[0-9]/.test(formData.newPassword),
    hasSpecialChar: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(formData.newPassword),
    maxLength: formData.newPassword.length <= 100
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear errors when user types
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.currentPassword) {
      newErrors.currentPassword = t('profile.security.currentPasswordRequired');
    }

    if (!formData.newPassword) {
      newErrors.newPassword = t('profile.security.newPasswordRequired');
    } else {
      const missing = [];
      if (!passwordRequirements.minLength) missing.push(t('profile.security.requirementMinLength'));
      if (!passwordRequirements.hasUpperCase) missing.push(t('profile.security.requirementUpperCase'));
      if (!passwordRequirements.hasLowerCase) missing.push(t('profile.security.requirementLowerCase'));
      if (!passwordRequirements.hasNumber) missing.push(t('profile.security.requirementNumber'));
      if (!passwordRequirements.hasSpecialChar) missing.push(t('profile.security.requirementSpecialChar'));
      if (!passwordRequirements.maxLength) missing.push('less than 100 ' + t('common.characters'));

      if (missing.length > 0) {
        newErrors.newPassword = `${t('profile.security.passwordMustContain')} ${missing.join(', ')}`;
      }
    }

    if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = t('profile.security.passwordsDoNotMatch');
    }

    if (formData.currentPassword === formData.newPassword) {
      newErrors.newPassword = t('profile.security.passwordMustBeDifferent');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      onSave({
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword
      });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label htmlFor="currentPassword" className="block text-sm font-medium text-shayek-light-700 dark:text-shayek-dark-700 mb-2">
          {t('profile.security.currentPassword')} *
        </label>
        <div className="relative">
          <input
            id="currentPassword"
            name="currentPassword"
            type={showPasswords.current ? "text" : "password"}
            value={formData.currentPassword}
            onChange={handleChange}
            required
            className={`w-full px-4 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-shayek-primary focus:border-transparent bg-white dark:bg-shayek-dark-50 text-shayek-light-900 dark:text-shayek-dark-900 ${
              errors.currentPassword ? 'border-red-500' : 'border-shayek-light-300 dark:border-shayek-dark-200'
            }`}
          />
          <button
            type="button"
            onClick={() => setShowPasswords(prev => ({ ...prev, current: !prev.current }))}
            className="absolute inset-y-0 right-0 flex items-center pr-3 text-shayek-light-600 dark:text-shayek-dark-600"
          >
            {showPasswords.current ? (
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.29 3.29m13.42 13.42l-3.29-3.29M3 3l13.42 13.42" />
              </svg>
            ) : (
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            )}
          </button>
        </div>
        {errors.currentPassword && (
          <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.currentPassword}</p>
        )}
      </div>

      <div>
        <label htmlFor="newPassword" className="block text-sm font-medium text-shayek-light-700 dark:text-shayek-dark-700 mb-2">
          {t('profile.security.newPassword')} *
        </label>
        <div className="relative">
          <input
            id="newPassword"
            name="newPassword"
            type={showPasswords.new ? "text" : "password"}
            value={formData.newPassword}
            onChange={handleChange}
            required
            className={`w-full px-4 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-shayek-primary focus:border-transparent bg-white dark:bg-shayek-dark-50 text-shayek-light-900 dark:text-shayek-dark-900 ${
              errors.newPassword ? 'border-red-500' : 'border-shayek-light-300 dark:border-shayek-dark-200'
            }`}
          />
          <button
            type="button"
            onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
            className="absolute inset-y-0 right-0 flex items-center pr-3 text-shayek-light-600 dark:text-shayek-dark-600"
          >
            {showPasswords.new ? (
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.29 3.29m13.42 13.42l-3.29-3.29M3 3l13.42 13.42" />
              </svg>
            ) : (
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            )}
          </button>
        </div>
        {formData.newPassword && (
          <div className="mt-2 p-3 bg-shayek-light-50 dark:bg-shayek-dark-50 rounded-lg border border-shayek-light-300 dark:border-shayek-dark-200">
            <p className="text-xs font-semibold text-shayek-light-700 dark:text-shayek-dark-700 mb-2">{t('profile.security.passwordRequired')}</p>
            <ul className="space-y-1 text-xs">
              <li className={`flex items-center ${passwordRequirements.minLength ? 'text-green-600 dark:text-green-400' : 'text-shayek-light-600 dark:text-shayek-dark-600'}`}>
                <span className={`mr-2 ${passwordRequirements.minLength ? 'text-green-600 dark:text-green-400' : ''}`}>
                  {passwordRequirements.minLength ? '✓' : '○'}
                </span>
                {t('profile.security.requirementMinLength')}
              </li>
              <li className={`flex items-center ${passwordRequirements.hasUpperCase ? 'text-green-600 dark:text-green-400' : 'text-shayek-light-600 dark:text-shayek-dark-600'}`}>
                <span className={`mr-2 ${passwordRequirements.hasUpperCase ? 'text-green-600 dark:text-green-400' : ''}`}>
                  {passwordRequirements.hasUpperCase ? '✓' : '○'}
                </span>
                {t('profile.security.requirementUpperCase')}
              </li>
              <li className={`flex items-center ${passwordRequirements.hasLowerCase ? 'text-green-600 dark:text-green-400' : 'text-shayek-light-600 dark:text-shayek-dark-600'}`}>
                <span className={`mr-2 ${passwordRequirements.hasLowerCase ? 'text-green-600 dark:text-green-400' : ''}`}>
                  {passwordRequirements.hasLowerCase ? '✓' : '○'}
                </span>
                {t('profile.security.requirementLowerCase')}
              </li>
              <li className={`flex items-center ${passwordRequirements.hasNumber ? 'text-green-600 dark:text-green-400' : 'text-shayek-light-600 dark:text-shayek-dark-600'}`}>
                <span className={`mr-2 ${passwordRequirements.hasNumber ? 'text-green-600 dark:text-green-400' : ''}`}>
                  {passwordRequirements.hasNumber ? '✓' : '○'}
                </span>
                {t('profile.security.requirementNumber')}
              </li>
              <li className={`flex items-center ${passwordRequirements.hasSpecialChar ? 'text-green-600 dark:text-green-400' : 'text-shayek-light-600 dark:text-shayek-dark-600'}`}>
                <span className={`mr-2 ${passwordRequirements.hasSpecialChar ? 'text-green-600 dark:text-green-400' : ''}`}>
                  {passwordRequirements.hasSpecialChar ? '✓' : '○'}
                </span>
                {t('profile.security.requirementSpecialChar')}
              </li>
            </ul>
          </div>
        )}
        {errors.newPassword && (
          <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.newPassword}</p>
        )}
      </div>

      <div>
        <label htmlFor="confirmPassword" className="block text-sm font-medium text-shayek-light-700 dark:text-shayek-dark-700 mb-2">
          {t('profile.security.confirmPassword')} *
        </label>
        <div className="relative">
          <input
            id="confirmPassword"
            name="confirmPassword"
            type={showPasswords.confirm ? "text" : "password"}
            value={formData.confirmPassword}
            onChange={handleChange}
            required
            className={`w-full px-4 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-shayek-primary focus:border-transparent bg-white dark:bg-shayek-dark-50 text-shayek-light-900 dark:text-shayek-dark-900 ${
              errors.confirmPassword ? 'border-red-500' : 'border-shayek-light-300 dark:border-shayek-dark-200'
            }`}
          />
          <button
            type="button"
            onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
            className="absolute inset-y-0 right-0 flex items-center pr-3 text-shayek-light-600 dark:text-shayek-dark-600"
          >
            {showPasswords.confirm ? (
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.29 3.29m13.42 13.42l-3.29-3.29M3 3l13.42 13.42" />
              </svg>
            ) : (
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            )}
          </button>
        </div>
        {errors.confirmPassword && (
          <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.confirmPassword}</p>
        )}
      </div>

      <div className="flex justify-end space-x-3">
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          className="px-4 py-2 border border-shayek-light-300 dark:border-shayek-dark-200 text-shayek-light-700 dark:text-shayek-dark-700 rounded-lg hover:bg-shayek-light-50 dark:hover:bg-shayek-dark-50 focus:outline-none focus:ring-2 focus:ring-shayek-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {t('common.cancel')}
        </button>
        <button
          type="submit"
          disabled={loading}
          className="px-4 py-2 bg-shayek-primary dark:bg-shayek-primary-600 text-white rounded-lg hover:bg-shayek-primary-600 dark:hover:bg-shayek-red-light focus:outline-none focus:ring-2 focus:ring-shayek-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {loading ? t('profile.security.changing') : t('profile.security.changePasswordButton')}
        </button>
      </div>
    </form>
  );
};

export default ChangePasswordForm;

